import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatchWithSimpleAlign,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class UploadFilePropsBuilder extends PropsBuilder<'uploadFile'> {
  override setParentContentProps = (
    elementProps: PartialPropsOf<'uploadFile'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'uploadFile'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatchWithSimpleAlign());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)
    ) {
      this.patchProps({ content: { title: { align: overrides.label.align } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'uploadFile'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'uploadFile'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({
        style: {
          general: { title: undefined },
          upload: {
            label: undefined,
            font: { fontFamily: undefined, fontSize: undefined, style: undefined },
            fill: undefined,
          },
        },
      });
      return this;
    }

    const { titleLabel, fieldLabel, fieldBox } = overrides;

    if (shouldPatchProp(titleLabel?.color, 'style.general.title', elementProps, initialValues)) {
      this.patchProps({ style: { general: { title: titleLabel.color } } });
    }

    if (
      shouldPatchProp(
        fieldLabel?.fontFamily,
        'style.upload.font.fontFamily',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ style: { upload: { font: { fontFamily: fieldLabel.fontFamily } } } });
    }
    if (
      shouldPatchProp(
        fieldLabel?.fontSize,
        'style.upload.font.fontSize',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ style: { upload: { font: { fontSize: fieldLabel.fontSize } } } });
    }
    if (
      shouldPatchProp(fieldLabel?.style, 'style.upload.font.style', elementProps, initialValues)
    ) {
      this.patchProps({ style: { upload: { font: { style: fieldLabel.style } } } });
    }
    if (shouldPatchProp(fieldLabel?.color, 'style.upload.label', elementProps, initialValues)) {
      this.patchProps({ style: { upload: { label: fieldLabel.color } } });
    }

    if (shouldPatchProp(fieldBox?.accent, 'style.upload.fill', elementProps, initialValues)) {
      this.patchProps({ style: { upload: { fill: fieldBox.accent } } });
    }

    return this;
  };

  constructor(props: PartialPropsOf<'uploadFile'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
